/**
 * Speech-to-Text Transcription
 *
 * Transcribes voice messages using the Web Speech API.
 * Provides real-time transcription during recording.
 */

import type { SpeechToTextResult } from '@/lib/types'

// Extend Window for SpeechRecognition types
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: Event & { error: string }) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance
  }
}

export interface SpeechToTextOptions {
  language?: string
  onInterimResult?: (result: SpeechToTextResult) => void
  onFinalResult?: (result: SpeechToTextResult) => void
  onError?: (error: Error) => void
}

let recognition: SpeechRecognitionInstance | null = null
let isListening = false
let finalTranscript = ''
let interimTranscript = ''

/**
 * Check if Web Speech API is available
 */
export function isSpeechRecognitionAvailable(): boolean {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
}

/**
 * Get the SpeechRecognition constructor
 */
function getSpeechRecognition(): new () => SpeechRecognitionInstance {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!SpeechRecognition) {
    throw new Error('Speech recognition not available')
  }
  return SpeechRecognition
}

/**
 * Start speech recognition
 */
export function startSpeechRecognition(options: SpeechToTextOptions = {}): void {
  if (isListening) {
    console.warn('[STT] Already listening')
    return
  }

  if (!isSpeechRecognitionAvailable()) {
    options.onError?.(new Error('Speech recognition not available in this browser'))
    return
  }

  const {
    language = 'en-US',
    onInterimResult,
    onFinalResult,
    onError,
  } = options

  try {
    const SpeechRecognition = getSpeechRecognition()
    recognition = new SpeechRecognition()

    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = language
    recognition.maxAlternatives = 1

    finalTranscript = ''
    interimTranscript = ''

    recognition.onstart = () => {
      isListening = true
      console.log('[STT] Started listening')
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result) {
          const alternative = result[0]
          if (alternative) {
            if (result.isFinal) {
              finalTranscript += alternative.transcript + ' '
              onFinalResult?.({
                transcript: finalTranscript.trim(),
                confidence: alternative.confidence,
                isFinal: true,
              })
            } else {
              interimTranscript += alternative.transcript
              onInterimResult?.({
                transcript: finalTranscript + interimTranscript,
                confidence: alternative.confidence,
                isFinal: false,
              })
            }
          }
        }
      }
    }

    recognition.onerror = (event) => {
      console.error('[STT] Error:', event.error)
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        onError?.(new Error(`Speech recognition error: ${event.error}`))
      }
    }

    recognition.onend = () => {
      isListening = false
      console.log('[STT] Stopped listening')

      // If we have any transcript, send final result
      if (finalTranscript.trim()) {
        onFinalResult?.({
          transcript: finalTranscript.trim(),
          confidence: 0.8, // Default confidence when recognition ends
          isFinal: true,
        })
      }
    }

    recognition.start()
  } catch (error) {
    console.error('[STT] Failed to start:', error)
    onError?.(error instanceof Error ? error : new Error(String(error)))
  }
}

/**
 * Stop speech recognition
 */
export function stopSpeechRecognition(): SpeechToTextResult | null {
  if (!recognition || !isListening) {
    return null
  }

  recognition.stop()
  isListening = false

  const result: SpeechToTextResult = {
    transcript: (finalTranscript + interimTranscript).trim(),
    confidence: 0.8, // Default confidence
    isFinal: true,
  }

  recognition = null
  finalTranscript = ''
  interimTranscript = ''

  return result.transcript ? result : null
}

/**
 * Abort speech recognition without returning results
 */
export function abortSpeechRecognition(): void {
  if (recognition) {
    recognition.abort()
    recognition = null
    isListening = false
    finalTranscript = ''
    interimTranscript = ''
  }
}

/**
 * Check if currently listening
 */
export function isSpeechListening(): boolean {
  return isListening
}

/**
 * Get current transcript (including interim results)
 */
export function getCurrentTranscript(): string {
  return (finalTranscript + interimTranscript).trim()
}

/**
 * Get confidence level description
 */
export function getConfidenceDescription(confidence: number): string {
  if (confidence > 0.9) return 'High confidence'
  if (confidence > 0.7) return 'Medium confidence'
  if (confidence > 0.5) return 'Low confidence'
  return 'Very low confidence'
}

/**
 * Get list of supported languages
 */
export function getSupportedLanguages(): { code: string; name: string }[] {
  return [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'es-MX', name: 'Spanish (Mexico)' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'ar-SA', name: 'Arabic' },
  ]
}
